import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { getProducts, type Product } from '../../utils/supabase-data';

export default function Home() {
  const { t } = useTranslation();
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    try {
      setLoading(true);
      const products = await getProducts();
      const featured = products.filter(p => p.badge).slice(0, 4);
      if (featured.length < 4) {
        const remaining = products.filter(p => !p.badge).slice(0, 4 - featured.length);
        setFeaturedProducts([...featured, ...remaining]);
      } else {
        setFeaturedProducts(featured);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white">
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://static.readdy.ai/image/332d61bd55ff2bc7860ffa3525be2f43/4a29576b7f461a77e135075d7c39b26d.png')",
            backgroundPosition: "center top"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        </div>

        {/* Mobile override image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat md:hidden"
          style={{
            backgroundImage: "url('https://static.readdy.ai/image/332d61bd55ff2bc7860ffa3525be2f43/272abbf6e1b5f64e2c5e68663c601a6f.jpeg')",
            backgroundPosition: "center top"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 w-full">
          <h1 
            className="mb-2 leading-none"
            style={{ 
              fontFamily: "'GoodDog Plain', cursive",
              fontSize: "clamp(3.5rem, 14vw, 11rem)",
              color: "#F2A7C3",
              textShadow: "3px 3px 0px rgba(0,0,0,0.4), -1px -1px 0px rgba(0,0,0,0.2)",
              fontWeight: 400,
              letterSpacing: "0.02em"
            }}
          >
            P'tit Voyou
          </h1>
          <p 
            className="mb-8 leading-none"
            style={{ 
              fontFamily: "'GoodDog Plain', cursive",
              fontSize: "clamp(2.5rem, 10vw, 8rem)",
              color: "#F2A7C3",
              textShadow: "3px 3px 0px rgba(0,0,0,0.4), -1px -1px 0px rgba(0,0,0,0.2)",
              fontWeight: 400,
              letterSpacing: "0.02em"
            }}
          >
            Reveur
          </p>
          <Link 
            to="/boutique"
            className="inline-block bg-brand-pink text-white px-8 py-3 md:px-10 md:py-4 text-sm font-bold tracking-widest hover:bg-brand-pink-dark transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('hero_button')}
          </Link>
        </div>
      </section>

      {/* Promo Banner */}
      <div className="bg-brand-pink text-white py-3 text-center px-4">
        <p className="text-xs sm:text-sm font-bold tracking-wide">
          <i className="ri-truck-line mr-2"></i>
          {t('home_promo_delivery').toUpperCase()} • {t('home_promo_payment').toUpperCase()}
        </p>
      </div>

      {/* Storytelling */}
      <section className="py-16 md:py-28 bg-gray-50 text-gray-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-20">
            {/* Texte à gauche */}
            <div className="w-full md:w-1/2 flex flex-col justify-center">
              <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: '#F2A7C3' }}>
                MAISON VOYOU
              </p>
              <div className="w-12 h-1 mb-8" style={{ background: '#F2A7C3' }}></div>
              <h2
                className="text-4xl md:text-6xl font-black mb-8 tracking-tight leading-none"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                {t('home_universe_title')}
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-5 text-gray-700 whitespace-pre-line">
                {t('home_universe_text1')}
              </p>
              <p className="text-base md:text-lg leading-relaxed text-gray-700 whitespace-pre-line">
                {t('home_universe_text2')}
              </p>
              <div className="mt-10">
                <Link
                  to="/boutique"
                  className="inline-block bg-black text-white px-8 py-3 text-sm font-bold tracking-widest hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer rounded-md"
                >
                  DÉCOUVRIR LA COLLECTION
                </Link>
              </div>
            </div>

            {/* Photo à droite */}
            <div className="w-full md:w-1/2 relative">
              <div className="w-full h-[420px] md:h-[620px] overflow-hidden rounded-lg">
                <img
                  src="https://static.readdy.ai/image/332d61bd55ff2bc7860ffa3525be2f43/763983c0da7937d2d0c2603f78dc34e5.jpeg"
                  alt="Maison Voyou - Collection"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Accent décoratif */}
              <div
                className="absolute -bottom-4 -right-4 w-24 h-24 md:w-40 md:h-40 rounded-lg -z-10"
                style={{ background: 'rgba(242,167,195,0.25)' }}
              ></div>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Histoire */}
      <section className="py-16 md:py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          {/* Titre centré */}
          <div className="text-center mb-12 md:mb-20">
            <p className="text-xs tracking-[0.4em] uppercase mb-3" style={{ color: '#F2A7C3' }}>
              {t('home_histoire_since')}
            </p>
            <h2
              className="text-4xl md:text-7xl font-black tracking-tight"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              {t('home_histoire_title')}
            </h2>
          </div>

          {/* Bloc photo + texte */}
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-20">
            {/* Photo */}
            <div className="w-full md:w-1/2 relative">
              <div className="w-full h-[420px] md:h-[600px] overflow-hidden rounded-lg">
                <img
                  src="https://static.readdy.ai/image/332d61bd55ff2bc7860ffa3525be2f43/5be118e544022f0050de5a7001b79fe5.jpeg"
                  alt="Notre Histoire - Voyou Rêveur"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Accent décoratif */}
              <div
                className="absolute -bottom-4 -left-4 w-24 h-24 md:w-40 md:h-40 rounded-lg -z-10"
                style={{ background: 'rgba(242,167,195,0.25)' }}
              ></div>
            </div>

            {/* Texte */}
            <div className="w-full md:w-1/2 flex flex-col justify-center">
              <div
                className="w-12 h-1 mb-8"
                style={{ background: '#F2A7C3' }}
              ></div>
              <h3
                className="text-2xl md:text-4xl font-black mb-6 leading-tight tracking-tight"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                {t('home_histoire_subtitle')}<br />
                <span style={{ color: '#F2A7C3' }}>{t('home_histoire_subtitle_accent')}</span>
              </h3>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-5">
                {t('home_histoire_p1')}
              </p>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-5">
                {t('home_histoire_p2')}
              </p>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed italic">
                {t('home_histoire_quote')}
              </p>
              <div className="mt-10">
                <a
                  href="/boutique"
                  className="inline-block bg-black text-white px-8 py-3 text-sm font-bold tracking-widest hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer rounded-md"
                >
                  {t('home_histoire_cta')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nouveautés */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-5xl font-black mb-3 tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {t('home_new_arrivals')}
            </h2>
            <p className="text-gray-600 text-sm">{t('home_new_arrivals_subtitle')}</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <i className="ri-loader-4-line text-5xl text-gray-300 animate-spin mb-4"></i>
              <p className="text-gray-600">{t('boutique_loading')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6" data-product-shop>
                {featuredProducts.map((product) => (
                  <Link 
                    key={product.id}
                    to={`/produit/${product.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden bg-gray-100 mb-3">
                      <div className="w-full h-52 sm:h-72 md:h-96">
                        <img 
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      {product.badge && (
                        <span className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-brand-pink text-white px-2 py-0.5 sm:px-3 sm:py-1 text-xs font-bold tracking-wide">
                          {product.badge}
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-xs sm:text-sm mb-1 sm:mb-2 tracking-wide leading-tight">{product.name}</h3>
                    <p className="text-base sm:text-lg font-black text-brand-pink-dark">{product.price} DT</p>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-8 md:mt-12">
                <Link 
                  to="/boutique"
                  className="inline-block bg-brand-pink text-white px-8 py-3 md:px-10 md:py-4 text-sm font-bold tracking-widest hover:bg-brand-pink-dark transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('home_see_collection')}
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
